import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { RELEASE_TABS, ReleaseTab, releaseTabPanelId } from './releaseTabProps';
import { releaseMessages } from './releaseMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './ReleasePage.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReleaseTabsProps {
  tab: ReleaseTab;
  /** "9 / 14": the made Kit slots and all slots; null while unknown. */
  kitFigure: string | null;
  /** The post files of the release on the board; null while unknown. */
  postCount: number | null;
  /** The planned posts of the release; null while unknown. */
  plannedCount: number | null;
  onChange: (tab: ReleaseTab) => void;
}

const LABELS = {
  overview: releaseMessages.tabOverview,
  kit: releaseMessages.tabKit,
  posts: releaseMessages.tabPosts,
  plan: releaseMessages.tabReleasePlan,
  campaign: releaseMessages.tabCampaign,
} as const;

/**
 * The tabs of the release page (design assets-rethink b1 `.ptabs`): Overview, Kit 9 / 14, Posts 12, Release
 * plan 7, Auto campaign.
 */
const ReleaseTabsComponent: React.FC<ReleaseTabsProps> = ({ tab, kitFigure, postCount, plannedCount, onChange }) => {
  const intl = useIntl();
  const count = (value: number | null): string | null =>
    value === null || value <= 0 ? null : intl.formatNumber(value);
  const figures: Record<ReleaseTab, string | null> = {
    overview: null,
    kit: kitFigure,
    posts: count(postCount),
    plan: count(plannedCount),
    // The page does not load the campaign of the song, so this tab has no figure.
    campaign: null,
  };

  return (
    <div className={styles.tabList} role="tablist" aria-label={intl.formatMessage(releaseMessages.tabs)}>
      {RELEASE_TABS.map((one) => {
        const figure = figures[one];
        return (
          <button
            key={one}
            type="button"
            role="tab"
            id={`${releaseTabPanelId(one)}-tab`}
            aria-selected={one === tab}
            aria-controls={releaseTabPanelId(one)}
            className={clsx(styles.tabButton, one === tab && styles.tabOn)}
            onClick={() => onChange(one)}
          >
            {intl.formatMessage(LABELS[one])}
            {figure !== null && <span className={styles.tabFig}>{figure}</span>}
          </button>
        );
      })}
    </div>
  );
};

export const ReleaseTabs = TypedMemo(ReleaseTabsComponent);
